import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { User, getToken, getUser, logout as logoutUser } from '../utils/auth';
import { setLogoutCallback } from '../utils/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { disconnect } = useWallet();

  useEffect(() => {
    // Don't re-initialize authentication if we're in the process of logging out
    if (isLoggingOut) {
      return;
    }

    const storedToken = getToken();
    const storedUser = getUser();

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(storedUser);
      setIsAuthenticated(true);
    }
  }, [isLoggingOut]);

  useEffect(() => {
    // Register logout callback for API error handling
    setLogoutCallback(logout);
  }, []);

  const login = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    setIsAuthenticated(true);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const logout = async () => {
    // Set logging out flag to prevent re-initialization
    setIsLoggingOut(true);
    
    // Clear all authentication state
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear localStorage
    logoutUser();
    
    // Disconnect wallet if connected
    try {
      await disconnect();
    } catch (error) {
      console.log('Wallet disconnect error (this is normal if wallet was not connected):', error);
    }
    
    // Force page reload to login page to ensure clean state
    window.location.href = '/login';
  };

  const value = {
    user,
    token,
    isAuthenticated,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};